import { Impact, RuleStatus, SchemaClause, ValidationSummary } from '@amzn/awscat-portfolio-assessment-types';
import { Button, Input, Select, SpaceBetween, TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { deepCopy, generatePluralStr } from '../../common/util';
import { groupItems, impactOptions, operators, severityFilterUnspecifiedOption, severityOptions, severitySortOrder } from './data-quality-constants';
import { RuleToDisplay } from './DataQualityRuleValidation';
import { AttributeOptions, OperatorOptions } from './interfaces';

/**
 * Return the string rule in either plural or singular form depending on the count
 * @param count 
 * @returns 
 */
export const generateRulePluralStr = (count: number): string => {
    return generatePluralStr(count, constants.RULE);
};

/**
 * Sort impact areas based on severity value
 * @param impactA 
 * @param impactB 
 * @returns 
 */
export const sortImpacts = (impactA: Impact, impactB: Impact) => {
    return severitySortOrder.indexOf(impactB.severity) - severitySortOrder.indexOf(impactA.severity);
};

/**
 * Returns the impact label associated with the given id
 * @param impactId 
 * @returns 
 */
export const getImpactLabel = (impactId: string): string => {
    const impact = impactOptions.find(impact => impact.id === impactId);
    return impact ? impact.label : impactId;
};

/**
 * Get the severity string by rule and impact area, null if it is not set
 * @param rule 
 * @param area 
 * @returns 
 */
export const getSeverity = (rule: RuleToDisplay, area: string): string | null => {
    const impact = rule.impacts?.find(impact => impact.area === area);
    return impact ? impact.severity : null;
};

/**
 * Get the label corresponding to the given severity
 * @param severity 
 * @returns 
 */
export const getSeverityLabel = (severity: string | null) => {
    if (severity) {
        const severityOption = severityOptions.find(option => option.value === severity);
        return severityOption ? severityOption.label : severityFilterUnspecifiedOption.label;
    } else {
        return severityFilterUnspecifiedOption.label;
    }
};

/**
 * Get the class names for impact badges to be displayed correctly
 * @param validationSummary 
 * @param rule 
 * @param area 
 * @returns 
 */
export const getImpactBadgeClassNames = (validationSummary: ValidationSummary | null, rule: RuleToDisplay, area: string): string => {
    const impactBadge = 'impactBadge';
    if (validationSummary && !validationSummary.rules[rule.ruleId]) {
        return `${impactBadge} passing-impact`;
    }
    const severity = getSeverity(rule, area);
    switch(severity) {
        case 'high':
            return `${impactBadge} high-impact`;
        case 'medium':
            return `${impactBadge} medium-impact`;
        case 'low':
            return `${impactBadge} low-impact`;
        default:
            return `${impactBadge} null-impact`;
    }
};

/**
 * Get a string represenation of either the number of issues found in the validation summary for a given rule,
 * or 'Unknown' if not set or 'Disabled' if the rule has been disabled
 * @param validationSummary 
 * @param rule 
 * @returns 
 */
export const getIssueCount = (validationSummary: ValidationSummary, rule: RuleToDisplay): string => {
    if (validationSummary.totalErrors == null) {
        return constants.UNKNOWN;
    } else if (rule.status === RuleStatus.ACTIVE) {
        return rule.ruleId in validationSummary.rules ? validationSummary.rules[rule.ruleId].toString() : '0';
    } else {
        return constants.DISABLED;
    }
};

export const getAllRulesPassHeader = (assetType: string): string => {
    return `${constants.NO_ERRORS_FOUND_HEADER_0} ${assetType}${constants.NO_ERRORS_FOUND_HEADER_1}`;
};

export const getRulesHasValidationErrorsHeader = (totalErrors: number, assetType: string): string => {
    return `${constants.ERRORS_FOUND_HEADER_0} ${totalErrors} 
        ${constants.ERRORS_FOUND_HEADER_1} ${assetType} ${constants.ERRORS_FOUND_HEADER_2}`;
};

export const getRulesHasLoadingErrorDescription = (rulesCount: number): string => {
    let str = constants.ERROR_LOADING_DESCRIPTION;
    if (rulesCount > 0) {
        str = `${str} ${constants.ERROR_LOADING_IF_RULES_EXIST}`;
    }
    return `${str} ${constants.ERROR_LOADING_USE_FEEDBACK}`;
};

export const getPassingRulesLabel = (arePassingRulesShowing: boolean, ruleCount: number): string => {
    return arePassingRulesShowing ? `${constants.HIDE} ${ruleCount} ${constants.PASSING_RULES}`
        : `${constants.SHOW} ${ruleCount} ${constants.PASSING_RULES}`;
};

export const getDisabledRulesLabel = (areDisabledRulesShowing: boolean, ruleCount: number): string => {
    return areDisabledRulesShowing ? `${constants.HIDE} ${ruleCount} ${constants.DISABLED_RULES}`
        : `${constants.SHOW} ${ruleCount} ${constants.DISABLED_RULES}`;
};

/**
 * Based on the selected operator, value may be disabled and set to an empty string
 * @param item 
 * @param attributeOptions 
 * @param onChange 
 * @returns 
 */
const isValueDisabled = (
    item: SchemaClause, attributeOptions: AttributeOptions[], onChange: (oldItem: SchemaClause, newItem: SchemaClause) => void
): boolean => {
    const option = getOperatorOptions(item, attributeOptions).find(option => option.id === item.operator);
    if (option && option.noValue) {
        if (item.value != '') {
            onChange(item, {...item, value: ''});
        }
        return true;
    }
    return false;
};

/**
 * Returns the JSX Element for the clause builder row action items - delete and copy
 * @param deleteClause 
 * @param copyClause 
 * @returns 
 */
const getActionItems = (deleteClause: () => void, copyClause: () => void): JSX.Element => {
    return (
        <SpaceBetween size='s' direction='horizontal'>
            <Button 
                iconName='close'
                variant='inline-icon'
                onClick={deleteClause}
            />
            <Button
                iconName='copy'
                variant='inline-icon'
                onClick={copyClause}
            />
        </SpaceBetween>
    );
};

/**
 * Returns the list of options for the operator select based on data type, string or numbers
 * @param item 
 * @param attributeOptions 
 * @returns 
 */
export const getOperatorOptions = (item: SchemaClause, attributeOptions: AttributeOptions[]): OperatorOptions[] => {
    if (!item.attribute || item.attribute === '') {
        return [];
    }
    //attributeOptions uses the attributeType (server, application, or database) and attribute name, separated by a colon,
    // to ensure unique values in the attribute list
    const selectedAttribute = attributeOptions.find(option => option.value === `${item.attributeType}:${item.attribute}`);
    if (selectedAttribute) {
        if (selectedAttribute.dataType === 'string' || selectedAttribute.dataType === 'text') {
            return operators.strings;
        } else {
            return operators.numbers;
        }
    }
    return operators.strings;
};

/**
 * Returns the column definitions for the clause builder table
 * @param isDefault 
 * @param attributeOptions 
 * @param onChange 
 * @returns 
 */
export const getClauseBuilderColumns = (
    isDefault: boolean, attributeOptions: AttributeOptions[], onChange: (oldItem: SchemaClause, newItem?: SchemaClause, isCopy?: boolean) => void
): TableProps.ColumnDefinition<SchemaClause>[] => {
    const columns: TableProps.ColumnDefinition<SchemaClause>[] = [
        {
            id: 'firstGroup',
            header: constants.GROUP,
            cell: item => {
                const selectedGroup = groupItems.find(option => option.value === item.firstGroup);
                return (
                    <Select 
                        selectedOption={selectedGroup ?? null}
                        options={groupItems}
                        onChange={({ detail }) => {
                            if (detail.selectedOption.value === 'null') {
                                onChange(item, {...item, firstGroup: null});
                            } else {
                                onChange(item, {...item, firstGroup: detail.selectedOption.value ?? null});
                            }
                        }}
                        expandToViewport
                    />
                );
            }
        },
        {
            id: 'attribute',
            header: constants.ATTRIBUTE,
            cell: item => {
                const selectedAttribute = attributeOptions.find(option => (option.value === `${item.attributeType}:${item.attribute}`));
                return (
                    <Select
                        selectedOption={selectedAttribute ?? null}
                        options={attributeOptions}
                        onChange={({ detail }) => {
                            const newSelection = attributeOptions.find(option => option.value === detail.selectedOption.value);
                            const newItem: SchemaClause = deepCopy(item);
                            newItem.attribute = detail.selectedOption.value?.replace(`${newSelection?.type}:`, '') ?? '';
                            if (detail.selectedOption.value === '') {
                                newItem.attributeType = null;
                            } else {
                                newItem.attributeType = newSelection?.type ?? null;
                            }
                            onChange(item, newItem);
                        }}
                        expandToViewport
                        filteringType='auto'
                    />
                );
            }
        },
        {
            id: 'operator',
            header: constants.OPERATOR,
            cell: item => {
                const selectedOperator = getOperatorOptions(item, attributeOptions).find(option => option.value === item.operator);
                
                return (
                    <Select
                        selectedOption={selectedOperator ?? null}
                        options={getOperatorOptions(item, attributeOptions)}
                        onChange={({ detail }) => {
                            onChange(item, {...item, operator: detail.selectedOption.value ?? null});
                        }}
                        expandToViewport
                    />
                );
            }
        },
        {
            id: 'value',
            header: constants.VALUE,
            cell: item => {
                return (
                    <Input
                        value={item.value ?? ''}
                        onChange={({ detail }) => onChange(item, {...item, value: detail.value})}
                        disabled={isDefault || isValueDisabled(item, attributeOptions, onChange)}
                    />
                );
            }
        },
        {
            id: 'secondGroup',
            header: constants.GROUP,
            cell: item => {
                const selectedGroup = groupItems.find(option => option.value === item.secondGroup);
                return (
                    <Select 
                        selectedOption={selectedGroup ?? null}
                        options={groupItems}
                        onChange={({ detail }) => {
                            if (detail.selectedOption.value === 'null') {
                                onChange(item, {...item, secondGroup: null});
                            } else {
                                onChange(item, {...item, secondGroup: detail.selectedOption.value ?? null});
                            }
                        }}
                        expandToViewport
                    />
                );
            }
        },
        {
            id: 'action',
            header: constants.ACTION,
            cell: item => getActionItems(() => onChange(item), () => onChange(item, undefined, true))
        }
    ];

    return columns;
};
